import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import apiClient from '@/lib/api/apiClient';
import { LIST_INVESTMENTS_KEY } from '@/lib/api/investments';
import {
  CreateInvestmentRentalPeriodRequestBody,
  CreateInvestmentRentalPeriodResponse,
  ListInvestmentRentalPeriodsResponse,
  UpdateInvestmentRentalPeriodRequestBody,
  UpdateInvestmentRentalPeriodResponse,
  CreateInvestmentRoomRequestBody,
  CreateInvestmentRoomResponse,
  UpdateInvestmentRoomRequestBody,
  UpdateInvestmentRoomResponse,
  ListInvestmentRoomsResponse,
  GetRentReconciliationResponse,
} from '@inversor-hub/types';

export const INVESTMENT_RENTAL_PERIODS_KEY = 'investment-rental-periods';
export const INVESTMENT_ROOMS_KEY = 'investment-rooms';
export const RENT_RECONCILIATION_KEY = 'rent-reconciliation';

export function useListInvestmentRooms(investmentId?: string) {
  return useQuery({
    queryKey: [INVESTMENT_ROOMS_KEY, investmentId],
    queryFn: () => apiClient.get<ListInvestmentRoomsResponse>(`/investments/${investmentId}/rooms`),
    select: (response) => response.data.data,
    enabled: !!investmentId,
  });
}

export function useCreateInvestmentRoom(investmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInvestmentRoomRequestBody) => {
      const response = await apiClient.post<
        unknown,
        AxiosResponse<CreateInvestmentRoomResponse>,
        CreateInvestmentRoomRequestBody
      >(`/investments/${investmentId}/rooms`, data);
      return response.data.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ROOMS_KEY, investmentId] }),
  });
}

export function useUpdateInvestmentRoom(investmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      roomId,
      data,
    }: {
      roomId: string;
      data: UpdateInvestmentRoomRequestBody;
    }) => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<UpdateInvestmentRoomResponse>,
        UpdateInvestmentRoomRequestBody
      >(`/investments/${investmentId}/rooms/${roomId}`, data);
      return response.data.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ROOMS_KEY, investmentId] }),
  });
}

export function useGetRentReconciliation(investmentId?: string, month?: string) {
  return useQuery({
    queryKey: [RENT_RECONCILIATION_KEY, investmentId, month],
    queryFn: () =>
      apiClient.get<GetRentReconciliationResponse>(
        `/investments/${investmentId}/rent-reconciliation${month ? `?month=${month}` : ''}`
      ),
    select: (response) => response.data.data,
    enabled: !!investmentId,
  });
}

export function useListInvestmentRentalPeriods(investmentId?: string) {
  return useQuery({
    queryKey: [INVESTMENT_RENTAL_PERIODS_KEY, investmentId],
    queryFn: async () => {
      return apiClient.get<ListInvestmentRentalPeriodsResponse>(
        `/investments/${investmentId}/rental-periods`
      );
    },
    select: (response) => response.data.data,
    enabled: !!investmentId,
  });
}

export function useCreateInvestmentRentalPeriod(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvestmentRentalPeriodRequestBody) => {
      const response = await apiClient.post<
        unknown,
        AxiosResponse<CreateInvestmentRentalPeriodResponse>,
        CreateInvestmentRentalPeriodRequestBody
      >(`/investments/${investmentId}/rental-periods`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_RENTAL_PERIODS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [RENT_RECONCILIATION_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: ['investment', investmentId] });
    },
  });
}

export function useUpdateInvestmentRentalPeriod(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      periodId,
      data,
    }: {
      periodId: string;
      data: UpdateInvestmentRentalPeriodRequestBody;
    }) => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<UpdateInvestmentRentalPeriodResponse>,
        UpdateInvestmentRentalPeriodRequestBody
      >(`/investments/${investmentId}/rental-periods/${periodId}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_RENTAL_PERIODS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [RENT_RECONCILIATION_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: ['investment', investmentId] });
    },
  });
}

export function useDeleteInvestmentRentalPeriod(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (periodId: string) => {
      await apiClient.delete(`/investments/${investmentId}/rental-periods/${periodId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_RENTAL_PERIODS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [RENT_RECONCILIATION_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: ['investment', investmentId] });
    },
  });
}
